import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  keepPreviousData,
} from "@tanstack/react-query";
import { EventsService } from "@/api/events";
import { EVENTABLE_TYPE } from "@/constants/events";
import type {
  Event,
  GetEventsParams,
  GetEventsResponse,
  EventAthlete,
  UpdateMode,
} from "@/api/events/types";
import { queryOptions } from "@tanstack/react-query";
import type {
  EventCreationFormData,
  EventModel,
  VacancyCreationFormData,
} from "@/schemas/event";
import { EventsRepository } from "@/repositories/events";
import { queryKeys } from "./constants";

interface UseEventsOptions {
  enabled?: boolean;
}

export const useEvents = (
  params: GetEventsParams,
  options: UseEventsOptions = {},
) => {
  return useQuery<GetEventsResponse, Error>({
    queryKey: queryKeys.events.list(params),
    queryFn: () => EventsService.instance.getEvents(params),
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const eventQueryOptions = (gymId: number, eventId: number) =>
  queryOptions({
    queryKey: queryKeys.events.detail(eventId),
    queryFn: () => EventsRepository.instance.getEventDetail(gymId, eventId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useEvent = (gymId: number, eventId: number | null) =>
  useQuery<Event, Error>({
    queryKey:
      eventId != null
        ? ([...queryKeys.events.detail(eventId), "view"] as const)
        : (["events", "disabled"] as const),
    queryFn: () => EventsService.instance.getEvent(gymId, eventId!),
    enabled: !!gymId && !!eventId,
    staleTime: 5 * 60 * 1000,
  });

export const useApproveEvent = (gymId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: number }) =>
      EventsService.instance.approveEvent(gymId, eventId),
    onSuccess: (_, { eventId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.events.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.events.detail(eventId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.gymSchedules.all,
        }),
      ]),
  });
};

export const useRejectEvent = (gymId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: number; reason?: string }) =>
      EventsService.instance.rejectEvent(gymId, eventId, reason),
    onSuccess: (_, { eventId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.events.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.events.detail(eventId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.gymSchedules.all,
        }),
      ]),
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    {
      gymId: number;
      eventId: number;
      deleteMode?: UpdateMode;
    }
  >({
    mutationFn: ({ gymId, eventId, deleteMode }) =>
      EventsService.instance.deleteEvent(gymId, eventId, deleteMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.personalSchedules.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.gymSchedules.all,
      });
    },
  });
};

export const useEventAttendances = (
  gymId: number,
  eventId: number,
  search?: string,
) =>
  useQuery({
    queryKey: queryKeys.events.attendances(
      eventId,
      search ? { search } : undefined,
    ),
    queryFn: () =>
      EventsRepository.instance.getEventAttendances(gymId, eventId, search),
  });

export const useAddEventAttendance = (gymId: number, eventId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      athleteId,
      status,
      attendanceNotes,
    }: {
      athleteId: number;
      status: string;
      attendanceNotes?: string;
    }) =>
      EventsService.instance.addEventAttendance(
        gymId,
        eventId,
        athleteId,
        status,
        attendanceNotes,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.attendances(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.availableAthletes(eventId),
      });
    },
  });
};

export const useUpdateEventAttendance = (gymId: number, eventId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attendanceId,
      status,
      attendanceNotes,
    }: {
      attendanceId: number;
      status: string;
      attendanceNotes?: string;
    }) =>
      EventsService.instance.updateEventAttendance(
        gymId,
        eventId,
        attendanceId,
        status,
        attendanceNotes,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.attendances(eventId),
      });
    },
  });
};

export const useDeleteEventAttendance = (gymId: number, eventId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attendanceId: number) =>
      EventsService.instance.deleteEventAttendance(
        gymId,
        eventId,
        attendanceId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.attendances(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.availableAthletes(eventId),
      });
    },
  });
};

export const useAvailableAthletesForEvent = (
  gymId: number,
  eventId: number,
  search?: string,
  page = 1,
  perPage = 4,
): UseQueryResult<
  {
    data: EventAthlete[];
    meta: {
      currentPage: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  },
  Error
> => {
  return useQuery({
    queryKey: queryKeys.events.availableAthletes(eventId, {
      search,
      page,
      perPage,
    }),
    queryFn: () =>
      EventsRepository.instance.getAvailableAthletes(
        gymId,
        eventId,
        search,
        page,
        perPage,
      ),
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Event,
    Error,
    { gymId: number; form: EventCreationFormData; timezone: string }
  >({
    mutationFn: ({ gymId, form, timezone }) =>
      EventsRepository.instance.createEvent(gymId, form, timezone),

    onSuccess: (event) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.personalSchedules.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.gymSchedules.all,
      });
      if (event.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.physicalEvaluationSessions.list(
            event.eventable.athlete.id,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.athletes.all,
        });
      }
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation<
    EventModel,
    Error,
    {
      gymId: string;
      id: string;
      form: EventCreationFormData;
      timezone: string;
      updateMode?: UpdateMode;
    }
  >({
    mutationFn: ({ gymId, id, form, timezone, updateMode }) =>
      EventsRepository.instance.updateEvent(
        gymId,
        id,
        form,
        timezone,
        updateMode,
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.personalSchedules.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.gymSchedules.all,
      });
    },
  });
};

export const useCreateVacancy = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Event,
    Error,
    { gymId: number; form: VacancyCreationFormData }
  >({
    mutationFn: ({ gymId, form }) =>
      EventsRepository.instance.createVacancy(gymId, form),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.events.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.personalSchedules.all,
      });
    },
  });
};
